import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { isUUIDV4, parseGroupKeysFromGroupRowIds } from '@aline/common';
import type { ApolloCache, ApolloLink, Reference } from '@apollo/client';
import { gql, makeVar } from '@apollo/client';
import { useApolloClient } from '@apollo/client/react';
import type {
  DataGridPremiumProps,
  GridApi,
  GridFilterModel,
  GridGetRowsParams,
  GridGetRowsResponse,
  GridRowModel,
  GridSortModel,
} from '@mui/x-data-grid-premium';
import {
  GRID_ROOT_GROUP_ID,
  GridGetRowsError,
  GridLogicOperator,
  gridRowSelectionIdsSelector,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid-premium';
import { isEqual, omit } from 'lodash';
import type {
  GroupHeaderRow,
  OrphanRelationshipRow,
  ReportGridRow,
  ReportRow,
  ReportRowQuestionJob,
  UrlRelationshipRow,
} from 'src/components';
import { getIsGridState, getIsReportGridRow } from 'src/components';
import type { ReportGridState } from 'src/components/reports/helpers';
import { useStable } from 'src/editor/helpers/useStable';
import { Apollo } from 'src/gql/generated';
import { Sentry } from 'src/sentry';
import type { ReactSetStateTuple } from 'src/types';
import { reportGroupRowIdDecodeError } from 'src/utils/group-row-id';

import { ReportDataSourceCache } from './report-data-source-cache';
import type { WatchedReportRowsVariables } from './use-report-rows-cache-sync';
import {
  addWatchedReportRowsVariables,
  useReportRowsCacheSync,
} from './use-report-rows-cache-sync';
import { useOnChange } from './utils';

export { ReportDataSourceCache };

// Global state for adding new documents to the active report DataGrid
export const activeReportVar = makeVar<{
  apiRef: React.RefObject<GridApi | null>;
  reportId: UUID;
} | null>(null);

/**
 * Reports whose grid missed a refresh because no matching grid was active when
 * documents were added — e.g. an upload finished while the Documents pane was
 * hidden inside React.Activity (which tears down the activeReportVar effect on
 * every tab hide). The grid activation effect consumes this set so the rows
 * appear when the pane is shown again.
 */
const reportIdsPendingGridRefresh = new Set<UUID>();

export function markReportGridForRefresh(reportId: UUID): void {
  reportIdsPendingGridRefresh.add(reportId);
}

/**
 * Refreshes the DataGrid by clearing cache and triggering a fresh fetch.
 * Use this when new documents are added to ensure the grid shows complete, type-safe data.
 */
export function refreshDataGrid(apiRef: React.RefObject<GridApi | null>): void {
  apiRef.current?.dataSource?.cache?.clear();
  void apiRef.current?.dataSource?.fetchRows(GRID_ROOT_GROUP_ID, { skipCache: true });
}

export function useGetReportColumnOptions() {
  const { data, loading, refetch } = Apollo.useGetReportColumnOptionsQuery();
  return { loading, refetch, reportColumnOptions: data?.reportColumnOptions ?? [] };
}

export const defaultFilterModel: GridFilterModel = {
  items: [],
  logicOperator: GridLogicOperator.And,
  quickFilterValues: [],
};
export const defaultSortModel: GridSortModel = [
  { field: 'createdAt' satisfies keyof ReportGridRow, sort: 'desc' },
];

const DISPLAY_MODE_TO_GQL: Record<'flat' | 'grouped' | 'relationship', Apollo.DisplayMode> = {
  flat: Apollo.DisplayMode.Flat,
  grouped: Apollo.DisplayMode.Grouped,
  relationship: Apollo.DisplayMode.Relationship,
};

function buildParentPath(parentTreeRowId: string | null | undefined, treeRowId: string): string[] {
  return parentTreeRowId ? [parentTreeRowId, treeRowId] : [treeRowId];
}

type ReportGridDataSourceError = Parameters<
  NonNullable<DataGridPremiumProps['onDataSourceError']>
>[0];

/**
 * Puts the grid back on its feet after a root-level row load rejects in a
 * grouped view. There the grid runs its nested lazy-loading strategy, whose
 * sort and filter handlers turn the loading flag on before fetching and whose
 * root-rows handler returns on an error without touching state. The base data
 * source clears the flag only for its standard strategies, so the overlay
 * would stay up until some later load succeeds. Clearing the rows as well is
 * what the standard strategies do on error, so stale group headers are not
 * left behind under the empty overlay.
 *
 * A failure below the root is left alone: the grid clears that group's own
 * loading state and records the error on it.
 */
export function clearGridAfterFailedRootLoad(
  api: Pick<GridApi, 'setLoading' | 'setRows'> | null,
  error: ReportGridDataSourceError
): void {
  if (!api || !(error instanceof GridGetRowsError)) return;
  if (error.params.groupKeys?.length) return;
  api.setRows([]);
  api.setLoading(false);
}

export function useReportDataSource(
  reportId: UUID,
  options: {
    apiRef: React.RefObject<GridApi | null>;
    report: Pick<Apollo.Report, 'state'>;
    displayMode?: 'flat' | 'relationship';
    docVariant?: Apollo.DocumentVariant;
    /**
     * When true, only return documents where extraction finished but no relationships were found.
     */
    missingMatchesOnly?: boolean;
    trashed?: boolean;
    unconfirmedRelationshipsOnly?: boolean;
  }
): {
  clearReportDataSourceCaches: () => void;
  dataSource: DataGridPremiumProps['dataSource'];
  dataSourceCache: ReportDataSourceCache;
  documentCount: number;
  filterModelTuple: ReactSetStateTuple<GridFilterModel>;
  loadedRowCount: number;
  removeDocumentsFromDataSource: (documentIds: readonly UUID[]) => void;
  removeGroupFromDataSource: (args: { groupRowId: string; trashedCount: number }) => void;
  rowGroupingModelTuple: ReactSetStateTuple<string[]>;
  sortModelTuple: ReactSetStateTuple<GridSortModel>;
  onDataSourceError?: DataGridPremiumProps['onDataSourceError'];
} {
  const {
    apiRef,
    displayMode = 'flat',
    docVariant,
    missingMatchesOnly,
    report,
    trashed,
    unconfirmedRelationshipsOnly,
  } = options;
  const client = useApolloClient();
  const prevSortModelRef = useRef<GridSortModel | null>(null);
  const [documentCount, setDocumentCount] = useState<number>(0);
  const [loadedRowCount, setLoadedRowCount] = useState<number>(0);
  const prevFilterModelRef = useRef<GridFilterModel | null>(null);
  const dataSourceCache = useStable(() => new ReportDataSourceCache(), []);
  const filterTimeZone = useStable(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);
  const [sortModel, setSortModel] = useState<GridSortModel>(
    getIsGridState(report.state)
      ? (report.state.sorting?.sortModel ?? defaultSortModel)
      : defaultSortModel
  );
  const [filterModel, setFilterModel] = useState<GridFilterModel>(
    getIsGridState(report.state)
      ? (report.state.filter?.filterModel ?? defaultFilterModel)
      : defaultFilterModel
  );
  const [rowGroupingModel, setRowGroupingModel] = useState<string[]>(
    getIsGridState(report.state) ? (report.state.rowGrouping?.model ?? []) : []
  );
  /**
   * Every `reportRows` cache entry a row load has written. Grouped and
   * relationship views write one entry per expanded level, keyed by
   * `groupKeys` or `parentTreeRowId`, so a single watcher cannot see them all.
   */
  const [watchedRowsVariables, setWatchedRowsVariables] = useState<
    readonly WatchedReportRowsVariables[]
  >([]);

  /**
   * Determine the effective display mode for server-side operations.
   * Row grouping takes precedence over flat mode, but relationship mode takes precedence over grouping.
   */
  const effectiveDisplayMode =
    displayMode === 'relationship'
      ? 'relationship'
      : rowGroupingModel.length > 0
        ? 'grouped'
        : 'flat';

  useOnChange(() => {
    setDocumentCount(0);
    setLoadedRowCount(0);
    setWatchedRowsVariables([]);
    dataSourceCache.clear();
  }, [
    apiRef,
    effectiveDisplayMode,
    missingMatchesOnly,
    unconfirmedRelationshipsOnly,
    rowGroupingModel,
  ]);

  useEffect(() => {
    activeReportVar({ apiRef, reportId });
    if (reportIdsPendingGridRefresh.delete(reportId)) refreshDataGrid(apiRef);

    return () => {
      activeReportVar(null);
    };
  }, [apiRef, reportId]);

  useReportRowsCacheSync({ apiRef, dataSourceCache, watchedRowsVariables });

  const safeGetRows = useCallback(
    async (params: GridGetRowsParams): Promise<GridGetRowsResponse> => {
      const { end, filterModel, groupKeys, paginationModel, sortModel, start } = params;
      const isGroupedMode = effectiveDisplayMode === 'grouped';

      /**
       * If groupKeys has values, we're fetching children of a parent.
       * groupKeys is an array of ancestor keys leading to the parent.
       * In relationship mode: these are tree row IDs.
       * In grouped mode: these are group key values (e.g., ['Signed', 'NDA']).
       */
      const parentTreeKey =
        groupKeys && groupKeys.length > 0 && !isGroupedMode
          ? String(groupKeys[groupKeys.length - 1])
          : null;
      const sortChanged = !isEqual(prevSortModelRef.current, sortModel);
      const filterChanged = !isEqual(prevFilterModelRef.current, filterModel);
      const safePageSize = paginationModel?.pageSize ?? 50;
      const limit = typeof start === 'number' ? end - start + 1 : safePageSize;
      const offset = typeof start === 'number' ? start : 0;
      if (filterChanged || sortChanged) {
        dataSourceCache.clear();
        setLoadedRowCount(0);
      }

      prevSortModelRef.current = sortModel;
      prevFilterModelRef.current = filterModel;

      /**
       * All modes use MUI native lazy loading — pass MUI's viewport range
       * straight through to the server, no client-side delta or row
       * accumulation. The response drives the grid: grouped fetches return
       * `groupCount` (direct child rows at this level), flat/relationship return
       * `totalCount` (document total); whichever is set becomes MUI's per-level
       * rowCount for skeleton math + pagination.
       */
      const deltaOffset = offset;
      const deltaLimit = limit;

      const gqlDisplayMode = DISPLAY_MODE_TO_GQL[effectiveDisplayMode];

      /**
       * In grouped mode MUI passes group row IDs (e.g. ["status:DRAFT"]) but the
       * server expects the raw key values (["DRAFT"]). A key can contain both
       * id delimiters — free-text answers routinely do — so the row id is
       * parsed against the column at each depth rather than split.
       */
      const extractedGroupKeys =
        isGroupedMode && groupKeys && groupKeys.length > 0
          ? parseGroupKeysFromGroupRowIds(groupKeys, rowGroupingModel, {
              onDecodeError: reportGroupRowIdDecodeError,
            })
          : undefined;

      const variables: Apollo.GetReportRowsQueryVariables = {
        displayMode: gqlDisplayMode,
        docVariant,
        filterModel,
        filterTimeZone,
        // For grouped mode, pass the grouping fields and expansion path
        groupFieldIds: isGroupedMode ? rowGroupingModel : undefined,
        groupKeys: extractedGroupKeys,
        limit: deltaLimit,
        missingMatchesOnly,
        offset: deltaOffset,
        parentTreeRowId: effectiveDisplayMode === 'relationship' ? parentTreeKey : undefined,
        reportId,
        sortModel,
        trashed,
        unconfirmedRelationshipsOnly,
      };
      const { data, error } = await client.query<
        Apollo.GetReportRowsQuery,
        Apollo.GetReportRowsQueryVariables
      >({
        fetchPolicy: 'no-cache',
        query: Apollo.GetReportRowsDocument,
        variables,
      });
      /**
       * The client resolves a failed request instead of rejecting it
       * (`errorPolicy: 'all'`): the error comes back beside a `data` whose
       * `report` is null. Written to the cache, that null replaces the report
       * itself, since `GetReportToRestore` reads the same `report(reportId)`
       * field, and the page is left with no report to draw; once the grouping
       * is saved, on every reload. Rethrow when the error left no rows, so the
       * grid takes its error path and the cache keeps the report. A partial
       * result, rows with an error inside one of them, renders as it always has.
       */
      if (error && !data?.report?.reportRows) throw error;
      if (data) {
        client.cache.writeQuery<Apollo.GetReportRowsQuery, Apollo.GetReportRowsQueryVariables>({
          data,
          query: Apollo.GetReportRowsDocument,
          variables,
        });
        const watchedVariables = omit(variables, ['limit', 'offset']);
        setWatchedRowsVariables(prev =>
          addWatchedReportRowsVariables({
            isNewQuery: filterChanged || sortChanged,
            next: watchedVariables,
            prev,
          })
        );
      }

      const newRows = data?.report?.reportRows?.rows ?? [];
      const rowsWithInfo = newRows.flatMap<ReportRow>(row => {
        if (row.__typename === 'GroupHeaderRow') {
          return [
            {
              __typename: 'GroupHeaderRow' as const,
              childRowCount: row.childRowCount ?? null,
              documentCount: row.documentCount ?? 0,
              groupFieldId: row.groupFieldId,
              groupKey: row.groupKey ?? '',
              id: row.id,
              parentPath: buildParentPath(row.parentTreeRowId, row.id),
              parentTreeRowId: row.parentTreeRowId ?? null,
            } satisfies GroupHeaderRow,
          ];
        }

        if (row.__typename === 'OrphanRelationshipRow') {
          return [
            {
              __typename: 'OrphanRelationshipRow' as const,
              id: row.id,
              orphanedRelationship: row.orphanedRelationship,
              orphanedRelationshipDirection: row.orphanedRelationshipDirection,
              parentPath: buildParentPath(row.parentTreeRowId, row.id),
              parentTreeRowId: row.parentTreeRowId,
            } satisfies OrphanRelationshipRow,
          ];
        }

        if (row.__typename === 'UrlRelationshipRow') {
          return [
            {
              __typename: 'UrlRelationshipRow' as const,
              childUrl: row.urlRelationship.childUrl ?? '',
              id: row.id,
              parentPath: buildParentPath(row.parentTreeRowId, row.id),
              parentTreeRowId: row.parentTreeRowId,
              urlRelationship: row.urlRelationship,
            } satisfies UrlRelationshipRow,
          ];
        }

        if (!row.document) return [];

        const questionJobsByQuestionId = (row.document?.questionJobs ?? []).reduce<
          Record<UUID, ReportRowQuestionJob>
        >((acc, job) => {
          if (job?.question?.id) acc[job.question.id] = job;
          return acc;
        }, {});

        return {
          ...row.document,
          childRowCount: row.childRowCount ?? 0,
          documentId: row.documentId,
          id: row.id,
          parentPath: buildParentPath(row.parentTreeRowId, row.id),
          parentTreeRowId: row.parentTreeRowId ?? null,
          questionJobsByQuestionId,
          relationship: row.relationship ?? null,
          relationshipDirection: row.relationshipDirection ?? null,
        } satisfies ReportGridRow;
      });

      const totalDocumentCount = data?.report?.reportRows?.totalCount ?? 0;
      const groupCount = data?.report?.reportRows?.groupCount ?? null;
      // Only update document count for root level
      const isRootLevel = !parentTreeKey && (!groupKeys || groupKeys.length === 0);
      if (isRootLevel) {
        setDocumentCount(totalDocumentCount);
        /**
         * Track cumulative loaded rows. The DataGrid's virtual buffer
         * evicts rows outside the viewport, so gridDataRowIdsSelector undercounts.
         */
        setLoadedRowCount(prev => Math.max(prev, deltaOffset + newRows.length));
      }

      /**
       * Return the requested page. `groupCount` (grouped — direct child rows at
       * this level) or `totalDocumentCount` (flat/relationship — document total)
       * is the per-level rowCount MUI uses to pad skeletons and paginate. Native
       * owns the row tree, so we never accumulate. The footer's document total
       * is set separately above (root level) from `totalDocumentCount`.
       */
      return {
        rowCount: groupCount ?? totalDocumentCount,
        rows: rowsWithInfo,
      };
    },
    [
      client,
      dataSourceCache,
      effectiveDisplayMode,
      docVariant,
      filterTimeZone,
      missingMatchesOnly,
      reportId,
      trashed,
      rowGroupingModel,
      unconfirmedRelationshipsOnly,
    ]
  );
  /**
   * Records a failed row load. The reader hears about it from the Apollo error
   * link, which toasts a `UserInputError` verbatim ("Cannot group by: <column>"
   * for a grouping that outlived its column) and a generic line for anything
   * else, so a toast from here would repeat it.
   */
  const onDataSourceError = useCallback(
    (error: ReportGridDataSourceError) => {
      console.error(error);
      Sentry.captureException(error);
      clearGridAfterFailedRootLoad(apiRef.current, error);
    },
    [apiRef]
  );

  /**
   * Clears the DataGrid data-source cache + loaded-row count. Call after
   * mutating rows client-side (e.g. trash) so the next getRows refetches fresh.
   */
  const clearReportDataSourceCaches = useCallback(() => {
    dataSourceCache.clear();
    setLoadedRowCount(0);
  }, [dataSourceCache]);

  /**
   * Surgical cache eviction for mutations that remove a known set of documents
   * (trash, permanent-delete, restore). The grid row removal itself is driven
   * by the caller via `apiRef.updateRows` (see `removeDocumentsFromGridApi`);
   * here we keep the data-source cache + reactive counts in sync so the rows
   * don't reappear on the next viewport fetch and the controlled `rowCount`
   * shrinks without kicking the lazy loader.
   *
   * We resolve each document's grid row id(s) from the live tree (a doc can map
   * to several rows: one per group it appears in, plus its relationship-tree
   * compound id) so the cache eviction covers grouped/relationship modes, not
   * just flat where `rowId === documentId`.
   */
  const removeDocumentsFromDataSource = useCallback(
    (documentIds: readonly UUID[]): void => {
      if (!documentIds.length) return;

      const removedDocSet = new Set<string>(documentIds);
      const rowIdsToEvict = new Set<string>(documentIds);
      const api = apiRef.current;
      if (api) {
        for (const [rowId, row] of api.getRowModels()) {
          if (
            row &&
            'documentId' in row &&
            typeof row.documentId === 'string' &&
            removedDocSet.has(row.documentId)
          ) {
            rowIdsToEvict.add(String(rowId));
          }
        }
      }
      dataSourceCache.removeRows([...rowIdsToEvict]);

      const delta = documentIds.length;
      setDocumentCount(prev => Math.max(0, prev - delta));
      setLoadedRowCount(prev => Math.max(0, prev - delta));
    },
    [apiRef, dataSourceCache]
  );

  /**
   * Whole-group trash counterpart of {@link removeDocumentsFromDataSource}. The
   * group's members may be unloaded (collapsed group), so we can't key off
   * document IDs — instead we evict the loaded leaf doc rows under the group's
   * row-id prefix and drop `documentCount` by the server-confirmed `trashedCount`
   * (which covers the unloaded members too).
   */
  const removeGroupFromDataSource = useCallback(
    ({ groupRowId, trashedCount }: { groupRowId: string; trashedCount: number }): void => {
      const api = apiRef.current;
      const loadedLeafRowIds = api
        ? [...api.getRowModels()]
            .filter(
              ([rowId, row]) =>
                String(rowId).startsWith(`${groupRowId}/`) &&
                row &&
                'documentId' in row &&
                typeof row.documentId === 'string'
            )
            .map(([rowId]) => String(rowId))
        : [];
      dataSourceCache.removeRows(loadedLeafRowIds);
      setDocumentCount(prev => Math.max(0, prev - trashedCount));
      setLoadedRowCount(prev => Math.max(0, prev - loadedLeafRowIds.length));
    },
    [apiRef, dataSourceCache]
  );

  const dataSource = useMemo(
    () => ({
      getChildrenCount: (row: GridRowModel) => {
        if (String(row.id).startsWith('skeleton-row-root-')) return 0;
        /**
         * Immediate child rows to reserve. `childRowCount` is the universal
         * "immediate child rows" field — subgroups for intermediate group
         * headers, relationship children for document rows. Group headers omit
         * it at the deepest level, where the child rows ARE the documents, so we
         * fall back to `documentCount`. Using the leaf-doc count for an
         * intermediate group would over-pad it with skeleton rows.
         */
        return row.childRowCount ?? row.documentCount ?? 0;
      },
      // Use row.id which is set to the unique tree row ID in safeGetRows
      getGroupKey: (row: GridRowModel) => String(row.id),
      getRows: safeGetRows,
    }),
    [safeGetRows]
  );

  return {
    clearReportDataSourceCaches,
    dataSource,
    dataSourceCache,
    documentCount,
    filterModelTuple: [filterModel, setFilterModel],
    loadedRowCount,
    onDataSourceError,
    removeDocumentsFromDataSource,
    removeGroupFromDataSource,
    rowGroupingModelTuple: [rowGroupingModel, setRowGroupingModel],
    sortModelTuple: [sortModel, setSortModel],
  };
}

export function useGetVisibleQuestionColumns() {
  const apiRef = useGridApiContext();
  const visibleColumns = apiRef.current.getVisibleColumns();
  return { visibleQuestionColumns: visibleColumns.filter(col => isUUIDV4(col.field)) };
}

export function useCreateReport() {
  const [createReport, { loading }] = Apollo.useCreateReportMutation();
  const safeCreateReport = useCallback(
    async (options: Parameters<Apollo.CreateReportMutationHookResult[0]>[0]) => {
      return createReport({
        ...options,
        update: (cache, { data }) => {
          if (!data) return;

          cache.modify({
            fields: {
              reports: existingReports => [
                ...existingReports,
                cache.writeFragment({
                  data: data.createReport.report,
                  fragment: gql`
                    fragment NewReport on Report {
                      __typename
                      id
                      name
                      createdAt
                    }
                  `,
                }),
              ],
            },
          });
        },
      });
    },
    [createReport]
  );

  return {
    createReport: safeCreateReport,
    loading,
  };
}

export function useDeleteReports(): {
  deleteReports: (reportIds: UUID[]) => Promise<ApolloLink.Result<Apollo.DeleteReportsMutation>>;
} {
  const [deleteReports] = Apollo.useDeleteReportsMutation();

  return {
    deleteReports: reportIds => {
      const deletedReportIdSet = new Set(reportIds);
      return deleteReports({
        /**
         * Refetch GetAgentThread so the chat's artifact bar flips in lockstep
         * with the report list. Without this, the bar can show a row pointing
         * to a hard-deleted report until the cache happens to refetch.
         */
        awaitRefetchQueries: true,
        /**
         * A chat panel with no thread open still holds a skipped GetAgentThread
         * query, registered with a blank id, and Apollo refetches skipped
         * queries that are named in `refetchQueries`. Sending that blank id
         * fails against the UUID argument, so leave that query alone.
         */
        onQueryUpdated: observableQuery =>
          observableQuery.queryName !== Apollo.namedOperations.Query.GetAgentThread ||
          Boolean(observableQuery.variables?.id),
        refetchQueries: [Apollo.namedOperations.Query.GetAgentThread],
        update: cache => {
          cache.modify({
            fields: {
              reports: (existingReports: readonly Reference[] = [], { readField }) => {
                return existingReports.filter(existingReport => {
                  const id = readField<UUID>('id', existingReport) ?? '';
                  return !deletedReportIdSet.has(id);
                });
              },
            },
          });
        },
        variables: { input: { reportIds } },
      });
    },
  };
}

export function useSaveReportState(): {
  loading: boolean;
  saveReport: (
    reportId: UUID,
    // Only save columns, filter, row grouping, and sorting as the other options we don't use and/or don't want to save.
    state: Pick<ReportGridState, 'columns' | 'filter' | 'rowGrouping' | 'sorting'>
  ) => Promise<ApolloLink.Result<Apollo.UpdateReportMutation>>;
} {
  const [updateReport, { loading }] = Apollo.useUpdateReportMutation();

  return {
    loading,
    saveReport: (reportId, state) =>
      updateReport({
        variables: { input: { reportId, reportProps: { state } } },
      }),
  };
}

export function useGetSelectedReportRows() {
  const apiRef = useGridApiContext();
  const selectedRowsMap = useGridSelector(apiRef, gridRowSelectionIdsSelector);
  const selectedReportRows = useMemo(() => {
    return [...selectedRowsMap.entries()]
      .filter(([id]) => !String(id).includes('skeleton'))
      .flatMap(([, row]) => {
        return getIsReportGridRow(row) ? [row] : [];
      });
  }, [selectedRowsMap]);

  return {
    apiRef,
    selectedReportRows,
  };
}

type UpdateCachedReportRowArgs = {
  action: {
    docRefs: Reference[];
    type: 'add';
  };
  cache: ApolloCache;
  reportId: UUID;
};

/**
 * Update the Apollo cache for Report.rows after uploads add new documents.
 *
 * Only the `add` variant survived the trash/delete surgical-eviction
 * refactor: the other variants wrote to `Report.rows` (a legacy field
 * distinct from `Report.reportRows` that the grid actually reads), which
 * made them no-ops for the grid. Callers removing documents should use
 * {@link evictDocumentsFromReportRowsCache} instead.
 */
export function updatedCachedReportRows({
  action,
  cache,
  reportId,
}: UpdateCachedReportRowArgs): void {
  cache.modify({
    fields: {
      rows: (ref: Reference, { readField }) => {
        const docsKey: keyof Apollo.RowsInfo = 'docs';
        const totalCountKey: keyof Apollo.RowsInfo = 'totalCount';
        const existingDocs = readField<Reference[]>(docsKey, ref) ?? [];
        const count = readField<number>(totalCountKey, ref) ?? 0;

        return {
          ...ref,
          __typename: 'RowsInfo',
          [docsKey]: [...action.docRefs, ...existingDocs],
          [totalCountKey]: count + action.docRefs.length,
        } satisfies Record<keyof Apollo.RowsInfo, unknown>;
      },
    },
    id: cache.identify({ __typename: 'Report', id: reportId }),
  });
}

/**
 * Surgically clean up the Apollo cache when documents are removed from a
 * report (trash, permanent delete, restore). Replaces the old
 * `updatedCachedReportRows({ type: 'remove' })` flow, which mutated
 * `Report.rows` — a stale legacy field the grid doesn't read.
 *
 * Trims every `Report.reportRows` store (each `keyArgs` variant has its
 * own store; Apollo calls `modify` once per variant so a single filter
 * covers all of them) by dropping `DocumentRow` entries whose scalar
 * `documentId` is in the removed set, and decrements `totalCount`
 * by the number actually removed. Uses `DocumentRow.documentId` (scalar)
 * rather than `DocumentRow.document.id` so the filter still works
 * correctly when the caller's mutation `update` fn has already evicted
 * the underlying `Document` entity (which would leave the `document`
 * reference dangling and `readField('id', ...)` returning undefined).
 *
 * Caller is responsible for `Document` eviction + `cache.gc()` — the
 * existing mutation `update` callbacks in `useDeleteTrashedDocuments` /
 * `useRestoreTrashedDocuments` handle that, and the trash-flow caller
 * invokes this separately.
 */
export function evictDocumentsFromReportRowsCache({
  cache,
  documentIds,
  reportId,
}: {
  cache: ApolloCache;
  documentIds: readonly UUID[];
  reportId: UUID;
}): void {
  if (!documentIds.length) return;

  const removedSet = new Set(documentIds);

  cache.modify({
    fields: {
      reportRows: (existing: Reference | undefined, { readField }) => {
        if (!existing) return existing;
        const rowsKey: keyof Apollo.ReportRowsInfo = 'rows';
        const totalCountKey: keyof Apollo.ReportRowsInfo = 'totalCount';
        const groupCountKey: keyof Apollo.ReportRowsInfo = 'groupCount';
        const existingRows = readField<Reference[]>(rowsKey, existing) ?? [];
        const totalCount = readField<number>(totalCountKey, existing) ?? 0;

        const keptRows = existingRows.filter(rowRef => {
          const typename = readField<string>('__typename', rowRef);
          if (typename !== 'DocumentRow') return true;
          const documentId = readField<UUID>('documentId', rowRef);
          return !(documentId && removedSet.has(documentId));
        });
        const removedCount = existingRows.length - keptRows.length;
        if (removedCount === 0) return existing;

        return {
          ...existing,
          __typename: 'ReportRowsInfo',
          [groupCountKey]: readField<number | null>(groupCountKey, existing) ?? null,
          [rowsKey]: keptRows,
          [totalCountKey]: Math.max(0, totalCount - removedCount),
        } satisfies Record<keyof Apollo.ReportRowsInfo, unknown>;
      },
    },
    id: cache.identify({ __typename: 'Report', id: reportId }),
  });
}

export function useSubscribeToReportUpdates(reportId: UUID): void {
  Apollo.useReportUpdatedSubscription({
    ignoreResults: true,
    variables: { reportId },
  });
}
